import axios from 'axios';

// IMPORTANT: Using same-origin to ensure API calls go to the correct backend
// This ensures the frontend calls the same domain it's hosted on (tnserviceshrms.com)
const BACKEND_URL = window.location.origin;

console.log('[API] Backend URL:', BACKEND_URL);

const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const login = (data) => api.post('/auth/login', data);
export const register = (data) => api.post('/auth/register', data);
export const getMe = () => api.get('/auth/me');
export const getLoginLogs = (params) => api.get('/auth/login-logs', { params });
export const getLoginLogsStats = () => api.get('/auth/login-logs/stats');

// Admin User Management
export const getAllUsers = () => api.get('/admin/users');
export const adminResetPassword = (userId, newPassword) => api.put(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
export const adminToggleUserStatus = (userId) => api.put(`/admin/users/${userId}/toggle-status`);
export const downloadUsersBulkTemplate = () => api.get('/admin/users/bulk-template', { responseType: 'blob' });
export const bulkUploadUsers = (formData) => api.post('/admin/users/bulk-upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});

// User Activity Logs
export const getActivityLogs = (params) => api.get('/activity-logs', { params });
export const getActivityLogsStats = () => api.get('/activity-logs/stats');
export const logActivity = (data) => api.post('/activity-logs', data);

// Departments
export const getDepartments = () => api.get('/departments');
export const createDepartment = (data) => api.post('/departments', data);

// Designations
export const getDesignations = () => api.get('/designations');
export const createDesignation = (data) => api.post('/designations', data);

// Employees
export const getEmployees = (params = {}) => api.get('/employees', { params });
export const getEmployeesPaginated = (params = {}) => api.get('/employees/paginated', { params });
export const createEmployee = (data, options = {}) => {
  const params = {};
  if (options.override) params.override = true;
  if (options.override_reason) params.override_reason = options.override_reason;
  return api.post('/employees', data, { params });
};
export const checkEmployeeDuplicate = (payload) => api.post('/employees/check-duplicate', payload);
export const reactivateEmployee = (id, data) => api.post(`/employees/${id}/reactivate`, data);
export const rehireEmployee = (id, data) => api.post(`/employees/${id}/rehire`, data);
export const getDuplicateOverrideLogs = (limit = 200) => api.get('/employee-admin/duplicate-override-logs', { params: { limit } });
export const getAllowDuplicateMobile = () => api.get('/employee-admin/settings/allow-duplicate-mobile');
export const setAllowDuplicateMobile = (allow) => api.put('/employee-admin/settings/allow-duplicate-mobile', { allow });
export const getDuplicateCleanupReport = (params = {}) => api.get('/employee-admin/duplicate-cleanup-report', { params });
export const markDuplicate = (data) => api.post('/employee-admin/duplicate-cleanup/mark', data);
export const unflagDuplicate = (id) => api.post(`/employee-admin/duplicate-cleanup/unflag/${id}`);
export const getDuplicateCleanupLogs = (limit = 200) => api.get('/employee-admin/duplicate-cleanup/logs', { params: { limit } });
export const autoFlagDuplicates = (data) => api.post('/employee-admin/duplicate-cleanup/auto-flag', data);
export const getEmployee = (id) => api.get(`/employees/${id}`);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const changeEmployeeEmail = (id, newEmail) => api.put(`/employees/${id}/change-email`, { new_email: newEmail });
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);
export const restoreEmployee = (id, reason) => api.post(`/employees/${id}/restore`, { reason });
export const getDeletedEmployees = (limit = 500) => api.get('/employee-admin/deleted-employees', { params: { limit } });
export const getEmployeeDeletionLogs = (limit = 200) => api.get('/employee-admin/deletion-logs', { params: { limit } });
export const bulkUploadEmployees = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/employees/bulk-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const downloadEmployeeTemplate = () => api.get('/employees/template', { responseType: 'blob' });

// Attendance
export const getAttendance = (employeeId) => {
  const params = employeeId ? { employee_id: employeeId } : {};
  return api.get('/attendance', { params });
};
export const createAttendance = (data) => api.post('/attendance', data);
export const bulkUploadAttendance = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/attendance/bulk-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const bulkMarkAttendance = (data) => api.post('/attendance/bulk-mark', data);
export const downloadAttendanceTemplate = () => api.get('/attendance/template', { responseType: 'blob' });
export const getAttendanceByDateRange = (startDate, endDate) => 
  api.get('/attendance/by-date-range', { params: { start_date: startDate, end_date: endDate } });
export const editAttendance = (attendanceId, data) => api.put(`/attendance/${attendanceId}`, data);
export const getAttendanceEditLogs = (params) => api.get('/attendance/edit-logs', { params });
export const getAttendanceEditLogsSummary = () => api.get('/attendance/edit-logs/summary');
export const getAttendanceSummary = (params) => api.get('/attendance/summary', { params });

// Leaves
export const getLeaves = (employeeId) => {
  const params = employeeId ? { employee_id: employeeId } : {};
  return api.get('/leaves', { params });
};
export const createLeave = (data) => api.post('/leaves', data);
export const previewLeaveSplit = (data) => api.post('/leaves/preview-split', data);
export const getLeaveQuotaSnapshot = (employeeId, year) =>
  api.get(`/leaves/quota-snapshot/${employeeId}`, year ? { params: { year } } : undefined);
export const getLwpForecast = (params = {}) => api.get('/leaves/lwp-forecast', { params });
export const approveLeave = (id) => api.put(`/leaves/${id}/approve`);
export const rejectLeave = (id) => api.put(`/leaves/${id}/reject`);
export const modifyLeave = (id, data) => api.put(`/leaves/${id}/modify`, data);
export const createBackdatedLeave = (data) => api.post('/leaves/backdated', data);
export const deleteLeave = (id) => api.delete(`/leaves/${id}`);
export const getLeaveAuditLog = (params) => api.get('/leaves/audit-log', { params });
export const getLeaveAuditStats = () => api.get('/leaves/audit-log/stats');

// Payroll
export const getPayroll = (employeeId) => {
  const params = employeeId ? { employee_id: employeeId } : {};
  return api.get('/payroll', { params });
};
export const createPayroll = (data) => api.post('/payroll', data);
export const bulkUploadPayroll = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/payroll/bulk-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const downloadPayrollTemplate = () => api.get('/payroll/template', { responseType: 'blob' });

// Statutory Compliance
export const getComplianceStates = () => api.get('/compliance/states');
export const getComplianceDashboard = () => api.get('/compliance/dashboard');
export const getComplianceMonthlySummary = (month, year) => 
  api.get('/compliance/monthly-summary', { params: { month, year } });
export const getPanIndiaSummary = (month, year) =>
  api.get('/compliance/pan-india-summary', { params: { month, year } });
export const getStateLaws = (state) => api.get(`/compliance/state-laws/${state}`);
export const downloadPfEcrReport = (month, year, format = 'excel') => 
  api.get('/compliance/report/pf-ecr', { params: { month, year, format }, responseType: 'blob' });
export const downloadEsiReport = (month, year, format = 'excel') => 
  api.get('/compliance/report/esi', { params: { month, year, format }, responseType: 'blob' });
export const downloadPtReport = (month, year, format = 'excel') => 
  api.get('/compliance/report/pt', { params: { month, year, format }, responseType: 'blob' });
export const downloadTdsReport = (quarter, year, format = 'excel') => 
  api.get('/compliance/report/tds', { params: { quarter, year, format }, responseType: 'blob' });
export const downloadLwfReport = (month, year, format = 'excel') => 
  api.get('/compliance/report/lwf', { params: { month, year, format }, responseType: 'blob' });
export const calculateStatutoryDeductions = (employeeId, grossSalary, basicSalary, state, employeeType) =>
  api.get('/compliance/calculate-statutory', { 
    params: { employee_id: employeeId, gross_salary: grossSalary, basic_salary: basicSalary, state, employee_type: employeeType } 
  });

// Performance Reviews
export const getReviews = (employeeId) => {
  const params = employeeId ? { employee_id: employeeId } : {};
  return api.get('/reviews', { params });
};
export const createReview = (data) => api.post('/reviews', data);

// Scheduler
export const getSchedulerStatus = () => api.get('/scheduler/status');
export const triggerReminders = () => api.post('/scheduler/trigger-reminders');

// Documents
export const getDocuments = (employeeId) => {
  const params = employeeId ? { employee_id: employeeId } : {};
  return api.get('/documents', { params });
};
export const createDocument = (data) => api.post('/documents', data);
export const uploadDocument = (file, employeeId, documentName, documentType) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('employee_id', employeeId);
  formData.append('document_name', documentName);
  formData.append('document_type', documentType);
  return api.post('/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};
export const deleteDocument = (id) => api.delete(`/documents/${id}`);
export const verifyDocument = (id, status, remarks) => api.put(`/documents/${id}/verify`, { status, remarks });
export const getConsolidatedDocuments = (params = {}) => api.get('/documents/consolidated', { params });
export const getDocumentTypes = () => api.get('/documents/types');

// Holidays
export const getHolidays = (year, month) => {
  const params = {};
  if (year) params.year = year;
  if (month) params.month = month;
  return api.get('/holidays', { params });
};
export const createHoliday = (data) => api.post('/holidays', data);
export const updateHoliday = (holidayId, data) => api.put(`/holidays/${holidayId}`, data);
export const deleteHoliday = (holidayId) => api.delete(`/holidays/${holidayId}`);
export const seedIndianHolidays = (year) => api.post(`/holidays/seed-indian-holidays?year=${year}`);

// Offer Letters
export const getOfferLetters = (params) => api.get('/offer-letters', params ? { params } : undefined);
export const getOfferLettersSummary = () => api.get('/offer-letters/summary');
export const getOfferLettersReport = () => api.get('/offer-letters/report');
export const bulkDownloadOfferLetters = (filters) => api.post('/offer-letters/bulk-download', filters, { responseType: 'blob' });
export const createOfferLetter = (data) => api.post('/offer-letters', data);
export const generateOfferLetterPDF = (offerId) => api.get(`/offer-letters/${offerId}/pdf`, { responseType: 'blob' });
export const updateOfferAnnexureEffectiveDate = (offerId, annexure_effective_date) =>
  api.patch(`/offer-letters/${offerId}/annexure-effective-date`, { annexure_effective_date });
export const bulkUpdateOfferAnnexureEffectiveDate = (offer_ids, annexure_effective_date) =>
  api.post('/offer-letters/bulk-update-annexure-effective-date', { offer_ids, annexure_effective_date });
export const downloadOfferAnnexure = (offerId) => api.get(`/offer-letters/${offerId}/annexure`, { responseType: 'blob' });
export const sendOfferAnnexure = (offerId, payload = {}) => api.post(`/offer-letters/${offerId}/annexure/send`, payload);
export const getOfferAnnexureLog = (offerId) => api.get(`/offer-letters/${offerId}/annexure/log`);
export const getOfferAnnexureHistory = (offerId) => api.get(`/offer-letters/${offerId}/annexure/history`);

// Zoho Sign (e-signature for standalone Annexure)
export const getZohoSignConfig = () => api.get('/zoho-sign/config');
export const sendOfferForESign = (offerId, payload = {}) => api.post(`/zoho-sign/offer-letters/${offerId}/send`, payload);
export const bulkSendOffersForESign = (payload) => api.post('/zoho-sign/offer-letters/bulk-send', payload);
export const listStaleESignOffers = (hours = 24) => api.get('/zoho-sign/offer-letters/stale', { params: { stale_threshold_hours: hours } });
export const remindStaleESignOffers = (payload) => api.post('/zoho-sign/offer-letters/remind-stale', payload);
export const countInProgressESignOffers = () => api.get('/zoho-sign/offer-letters/in-progress-count');
export const refreshESignStatuses = (payload = {}) => api.post('/zoho-sign/offer-letters/refresh-statuses', payload);
export const getOfferESignStatus = (offerId, refresh = false) => api.get(`/zoho-sign/offer-letters/${offerId}/status`, { params: { refresh } });
export const downloadSignedAnnexure = (offerId) => api.get(`/zoho-sign/offer-letters/${offerId}/signed-pdf`, { responseType: 'blob' });
export const recallOfferESign = (offerId, reason) => api.post(`/zoho-sign/offer-letters/${offerId}/recall`, null, { params: reason ? { reason } : {} });
export const remindOfferESign = (offerId) => api.post(`/zoho-sign/offer-letters/${offerId}/remind`);
export const getOfferESignLog = (offerId) => api.get(`/zoho-sign/offer-letters/${offerId}/log`);
export const sendOfferLetter = (offerId, email, cc = []) => api.post(`/offer-letters/${offerId}/send`, { email, cc });
export const bulkCreateOfferLetters = (data) => api.post('/offer-letters/bulk', data);
export const bulkSendOfferLetters = (offerIds, cc = []) => api.post('/offer-letters/bulk-send', { offer_ids: offerIds, cc });
export const downloadOfferLetterTemplate = () => api.get('/offer-letters/download-template', { responseType: 'blob' });
export const bulkUploadOfferLetters = (file, cc = []) => {
  const formData = new FormData();
  formData.append('file', file);
  if (cc && cc.length > 0) {
    formData.append('cc', JSON.stringify(cc));
  }
  return api.post('/offer-letters/bulk-upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Offer Letter Templates
export const getOfferLetterTemplates = () => api.get('/offer-letters/templates');
export const getOfferLetterTemplate = (id) => api.get(`/offer-letters/templates/${id}`);
export const createOfferLetterTemplate = (data) => api.post('/offer-letters/templates', data);
export const updateOfferLetterTemplate = (id, data) => api.put(`/offer-letters/templates/${id}`, data);
export const patchOfferLetterTemplate = (id, data) => api.patch(`/offer-letters/templates/${id}`, data);
export const previewTemplateClausesPdf = (id, data) => api.post(`/offer-letters/templates/${id}/preview-pdf`, data, { responseType: 'blob' });
export const previewTemplateLegalClausesPdf = (id) => api.get(`/offer-letters/templates/${id}/legal-clauses-pdf`, { responseType: 'blob' });
export const auditTemplateNoticePeriods = () => api.get(`/offer-letters/templates/notice-period-audit`);
export const auditTemplateNoticeContradictions = () => api.get(`/offer-letters/templates/notice-contradiction-audit`);
export const removeLegacyNoticeTerm = () => api.post(`/offer-letters/admin/remove-legacy-notice-term`);
export const duplicateOfferLetterTemplate = (id, data) => api.post(`/offer-letters/templates/${id}/duplicate`, data);
export const previewOfferEmailTemplate = (id, data) => api.post(`/offer-letters/templates/${id}/email-preview`, data);
export const sendTestOfferEmail = (id, data) => api.post(`/offer-letters/templates/${id}/send-test-email`, data);
export const listOfferEmailTemplateVersions = (id) => api.get(`/offer-letters/templates/${id}/email-versions`);

// Template annexures (admin-uploadable PDF attachments)
export const listTemplateAnnexures = (templateId) => api.get(`/offer-letters/templates/${templateId}/annexures`);
export const uploadTemplateAnnexure = (templateId, formData) => api.post(`/offer-letters/templates/${templateId}/annexures`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const patchTemplateAnnexure = (templateId, annexureId, data) => api.patch(`/offer-letters/templates/${templateId}/annexures/${annexureId}`, data);
export const deleteTemplateAnnexure = (templateId, annexureId) => api.delete(`/offer-letters/templates/${templateId}/annexures/${annexureId}`);
export const cloneTemplateAnnexure = (templateId, annexureId, data) => api.post(`/offer-letters/templates/${templateId}/annexures/${annexureId}/clone`, data);
export const attachHgipBulk = (params = {}) => api.post('/offer-letters/templates/attach-hgip-bulk', null, { params });
export const downloadTemplateAnnexureUrl = (templateId, annexureId) => `${api.defaults.baseURL}/offer-letters/templates/${templateId}/annexures/${annexureId}/download`;
export const deleteOfferLetterTemplate = (id) => api.delete(`/offer-letters/templates/${id}`);
export const seedDefaultTemplates = () => api.post('/offer-letters/templates/seed-default');

// Annexure Version History (DC / TL salary annexure PDFs)
export const listAnnexureVersions = (designation) => api.get('/offer-letters/annexures/versions', { params: designation ? { designation } : {} });
export const uploadAnnexureVersion = (formData) => api.post('/offer-letters/annexures/versions', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const rollbackAnnexureVersion = (versionId) => api.post(`/offer-letters/annexures/versions/${versionId}/rollback`);
export const editAnnexureText = (payload) => api.post('/offer-letters/annexures/versions/edit-text', payload);
export const previewAnnexureEdit = (payload) => api.post('/offer-letters/annexures/versions/preview-edit', payload);
export const downloadAnnexureVersionUrl = (versionId) => `${api.defaults.baseURL}/offer-letters/annexures/versions/${versionId}/download`;

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Company Settings
export const getCompanySettings = () => api.get('/company/settings');
export const updateCompanySettings = (data) => api.post('/company/settings', data);

// SMTP Settings
export const getSmtpSettings = () => api.get('/smtp/settings');
export const saveSmtpSettings = (data) => api.post('/smtp/settings', data);
export const testSmtpConnection = () => api.post('/smtp/test');

// Email Delivery Logs
export const getEmailLogs = (params) => api.get('/email-logs', { params });
export const getEmailLogStats = () => api.get('/email-logs/stats');
export const retryFailedEmail = (logId) => api.post(`/email-logs/${logId}/retry`);

// Data Sync (for production deployment)
export const syncAllData = (secretKey = 'tn-sync-all-2025') => 
  api.post(`/sync-all-data?secret_key=${secretKey}`);

// Statutory Registrations
export const getStatutoryRegistrations = () => api.get('/statutory-registrations');
export const getLwfStates = () => api.get('/compliance/lwf-states');

// TDS & Form 16
export const downloadForm16 = (employeeId, financialYear) => 
  api.get(`/tds/form-16/${employeeId}/${financialYear}`, { responseType: 'blob' });
export const downloadForm24Q = (quarter, financialYear) => 
  api.get(`/tds/form-24q/${quarter}/${financialYear}`, { responseType: 'blob' });

// Final Settlement
export const getFinalSettlements = (status, employeeId) => {
  const params = {};
  if (status) params.status = status;
  if (employeeId) params.employee_id = employeeId;
  return api.get('/final-settlements', { params });
};
export const getFinalSettlement = (id) => api.get(`/final-settlements/${id}`);
export const createFinalSettlement = (data) => api.post('/final-settlements', data);
export const approveFinalSettlement = (id) => api.put(`/final-settlements/${id}/approve`);
export const rejectFinalSettlement = (id, reason) => api.put(`/final-settlements/${id}/reject?reason=${encodeURIComponent(reason)}`);
export const markSettlementProcessed = (id) => api.put(`/final-settlements/${id}/process`);
export const downloadFinalSettlementPDF = (id) => api.get(`/final-settlements/${id}/pdf`, { responseType: 'blob' });
export const getFinalSettlementStats = () => api.get('/final-settlements/stats/summary');

// Payroll - Enhanced
export const autoCalculatePayroll = (employeeId, month, year) => 
  api.post(`/payroll/auto-calculate?employee_id=${employeeId}&month=${month}&year=${year}`);
export const downloadPayslip = (payrollId) => 
  api.get(`/payroll/${payrollId}/pdf`, { responseType: 'blob' });
export const bulkDownloadPayslips = (month, year, designationId, departmentId) => {
  const params = { month, year };
  if (designationId) params.designation_id = designationId;
  if (departmentId) params.department_id = departmentId;
  return api.get('/payroll/bulk-download', { params, responseType: 'blob' });
};
export const getPayrollAvailableFilters = (month, year, departmentId) => {
  const params = { month, year };
  if (departmentId) params.department_id = departmentId;
  return api.get('/payroll/available-filters', { params });
};
export const downloadSelectedPayslips = (payrollIds) => 
  api.post('/payroll/download-selected', { payroll_ids: payrollIds }, { responseType: 'blob' });

// Reimbursements
export const getReimbursements = (status, employeeId) => {
  const params = {};
  if (status) params.status = status;
  if (employeeId) params.employee_id = employeeId;
  return api.get('/reimbursements', { params });
};
export const createReimbursement = (data) => api.post('/reimbursements', data);
export const approveReimbursement = (id, remarks) => 
  api.put(`/reimbursements/${id}/approve${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`);
export const rejectReimbursement = (id, remarks) => 
  api.put(`/reimbursements/${id}/reject${remarks ? `?remarks=${encodeURIComponent(remarks)}` : ''}`);
export const getReimbursementStats = () => api.get('/reimbursements/stats');
export const uploadReimbursementReceipt = (id, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/reimbursements/${id}/upload-receipt`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Tax Declaration Documents
export const uploadTaxDocument = (category, file) => {
  const formData = new FormData();
  formData.append('category', category);
  formData.append('file', file);
  return api.post('/tax-declaration/upload-document', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getTaxDocuments = (employeeId, financialYear) => {
  const params = {};
  if (employeeId) params.employee_id = employeeId;
  if (financialYear) params.financial_year = financialYear;
  return api.get('/tax-declaration/documents', { params });
};
export const deleteTaxDocument = (documentId) => api.delete(`/tax-declaration/documents/${documentId}`);

// Exit Management
export const getExits = (status, exitType) => {
  const params = {};
  if (status) params.status = status;
  if (exitType) params.exit_type = exitType;
  return api.get('/exits', { params });
};
export const getExitDetails = (exitId) => api.get(`/exits/${exitId}`);
export const initiateExit = (data) => api.post('/exits', data);
export const updateExit = (exitId, data) => api.put(`/exits/${exitId}`, data);
export const updateClearance = (exitId, data) => api.put(`/exits/${exitId}/clearance`, data);
export const updateHandover = (exitId, data) => api.put(`/exits/${exitId}/handover`, data);
export const updateAssets = (exitId, data) => api.put(`/exits/${exitId}/assets`, data);
export const updateExitInterview = (exitId, data) => api.put(`/exits/${exitId}/exit-interview`, data);
export const calculateFnfSettlement = (exitId, data) => api.put(`/exits/${exitId}/fnf-settlement`, data);
export const getExitStats = () => api.get('/exits/stats/summary');
export const cancelExit = (exitId) => api.delete(`/exits/${exitId}`);

// Access Removal Management
export const getPendingAccessRemoval = () => api.get('/exits/pending-access-removal');
export const processAccessRemoval = () => api.post('/exits/process-access-removal');

// Employee Letters (Relieving, Experience, Domicile)
export const generateEmployeeLetter = (data) => api.post('/employee-letters/generate', data, { responseType: 'blob' });
export const getIssuedLetters = (params) => api.get('/employee-letters', { params });
export const getLetterStats = () => api.get('/employee-letters/stats');
export const getFnfReleasedEmployees = () => api.get('/employee-letters/fnf-released-employees');

// HR Letters (Warning, Termination, NCNS, Absconding, Short-Notice, Resignation Ack, Asset Recovery)
export const getHRLetterTypes = () => api.get('/hr-letters/types');
export const generateHRLetter = (data) => api.post('/hr-letters/generate', data, { responseType: 'blob' });
export const redownloadHRLetter = (letterId) => api.get(`/hr-letters/${letterId}/download`, { responseType: 'blob' });
export const getIssuedHRLetters = (params) => api.get('/hr-letters/issued', { params });
export const getHRLetterStats = () => api.get('/hr-letters/stats');

// Recruiter Performance
export const getRecruiterDashboard = (params) => api.get('/recruiter-performance/dashboard', { params });
export const getRecruiterAlerts = () => api.get('/recruiter-performance/alerts');
export const getRecruitersList = () => api.get('/recruiter-performance/recruiters');
export const getClientBillings = () => api.get('/recruiter-performance/client-billing');
export const createClientBilling = (data) => api.post('/recruiter-performance/client-billing', data);
export const updateClientBilling = (id, data) => api.put(`/recruiter-performance/client-billing/${id}`, data);
export const deleteClientBilling = (id) => api.delete(`/recruiter-performance/client-billing/${id}`);
export const getPlacements = (params) => api.get('/recruiter-performance/placements', { params });
export const createPlacement = (data) => api.post('/recruiter-performance/placements', data);
export const updatePlacement = (id, data) => api.put(`/recruiter-performance/placements/${id}`, data);
export const deletePlacement = (id) => api.delete(`/recruiter-performance/placements/${id}`);

// Ops Manager
export const getOpsTeam = () => api.get('/ops-manager/team');
export const assignToOpsTeam = (employeeIds) => api.post('/ops-manager/team/assign', { employee_ids: employeeIds });
export const removeFromOpsTeam = (employeeIds) => api.post('/ops-manager/team/remove', { employee_ids: employeeIds });
export const getOpsAttendance = (params) => api.get('/ops-manager/attendance', { params });
export const regularizeOpsAttendance = (data) => api.post('/ops-manager/attendance/regularize', data);
export const getOpsPendingLeaves = () => api.get('/ops-manager/leaves/pending');
export const opsLeaveAction = (leaveId, data) => api.put(`/ops-manager/leaves/${leaveId}/action`, data);
export const getOpsLeaveHistory = (params) => api.get('/ops-manager/leaves/history', { params });
export const getOpsPerformance = (params) => api.get('/ops-manager/performance', { params });

// ==================== Inventory Management ====================
export const getInventoryDashboard = () => api.get('/inventory/dashboard');

// Warehouses
export const getWarehouses = () => api.get('/inventory/warehouses');
export const createWarehouse = (data) => api.post('/inventory/warehouses', data);
export const updateWarehouse = (id, data) => api.put(`/inventory/warehouses/${id}`, data);
export const getWarehouseHandovers = (id) => api.get(`/inventory/warehouses/${id}/handovers`);
export const deleteWarehouse = (id) => api.delete(`/inventory/warehouses/${id}`);

// Item Types
export const getItemTypes = (params) => api.get('/inventory/item-types', { params });
export const createItemType = (data) => api.post('/inventory/item-types', data);
export const deleteItemType = (id) => api.delete(`/inventory/item-types/${id}`);

// Serialized Inventory Items
export const getInventoryItems = (params) => api.get('/inventory/items', { params });
export const getInventoryItem = (id) => api.get(`/inventory/items/${id}`);
export const createInventoryItem = (data) => api.post('/inventory/items', data);

// Bulk Batches
export const getStockBatches = (params) => api.get('/inventory/batches', { params });
export const createStockBatch = (data) => api.post('/inventory/batches', data);

// Bulk Inventory Intake (Excel)
export const downloadInventoryBulkTemplate = () => api.get('/inventory/bulk/items-template', { responseType: 'blob' });
export const bulkUploadInventory = (formData) => api.post('/inventory/bulk/items-upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});

// Transfer
export const transferInventory = (data) => api.post('/inventory/transfer', data);
export const downloadTransferBulkTemplate = () => api.get('/inventory/bulk/transfer-template', { responseType: 'blob' });
export const bulkUploadTransfer = (formData) => api.post('/inventory/bulk/transfer-upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' },
});

// Movements
export const getInventoryMovements = (params) => api.get('/inventory/movements', { params });

// Merchants
export const getMerchants = (params) => api.get('/inventory/merchants', { params });
export const getMerchant = (id) => api.get(`/inventory/merchants/${id}`);
export const createMerchant = (data) => api.post('/inventory/merchants', data);

// Returns
export const getReturns = (params) => api.get('/inventory/returns', { params });
export const createReturn = (data) => api.post('/inventory/returns', data);
export const actOnReturn = (id, data) => api.put(`/inventory/returns/${id}/action`, data);

// Recipients (for transfer dropdown)
export const getInventoryRecipients = (params) => api.get('/inventory/recipients', { params });
export const getInventoryKnownCities = () => api.get('/inventory/cities');
export const getInventoryRoleMappings = () => api.get('/inventory/role-mappings');
export const replaceInventoryRoleMappings = (mappings) => api.put('/inventory/role-mappings', { mappings });
export const getInventoryUsers = (params) => api.get('/inventory/users', { params });

// Reports
export const getPersonWiseReport = (params) => api.get('/inventory/reports/person-wise', { params });
export const getStockSummaryReport = (params) => api.get('/inventory/reports/stock-summary', { params });
export const getInactiveAssetsReport = (params) => api.get('/inventory/reports/inactive-assets', { params });
export const getMerchantActivationReport = (params) => api.get('/inventory/reports/merchant-activation', { params });
export const getScrapLeakageTrendsReport = (params) => api.get('/inventory/reports/scrap-leakage-trends', { params });
export const exportInventoryReport = (params) => api.get('/inventory/reports/export', { params, responseType: 'blob' });
export const sendLowStockAlert = (params) => api.post('/inventory/alerts/low-stock', null, { params });
export const getDistributionAnalytics = (params) => api.get('/inventory/reports/distribution-analytics', { params });
export const exportDistributionAnalytics = (params) => api.get('/inventory/reports/distribution-analytics/export', { params, responseType: 'blob' });

// Inventory Requests (FSE → TL → ASM approval chain)
export const createInventoryRequest = (data) => api.post('/inventory/requests', data);
export const getInventoryRequests = (params) => api.get('/inventory/requests', { params });
export const getInventoryRequest = (id) => api.get(`/inventory/requests/${id}`);
export const approveInventoryRequest = (id, data) => api.put(`/inventory/requests/${id}/approve`, data);
export const rejectInventoryRequest = (id, data) => api.put(`/inventory/requests/${id}/reject`, data);
export const fulfillInventoryRequest = (id, data) => api.put(`/inventory/requests/${id}/fulfill`, data);
export const cancelInventoryRequest = (id) => api.delete(`/inventory/requests/${id}`);

// eKYC Verification (Cashfree + Gemini OCR)
export const verifyPan = (data) => api.post('/ekyc/pan-verify', data);
export const verifyIfsc = (data) => api.post('/ekyc/ifsc-verify', data);
export const verifyBankAccount = (data) => api.post('/ekyc/bank-verify', data);
export const ocrDocument = (docType, file, employeeId) => {
  const fd = new FormData();
  fd.append('file', file);
  if (employeeId) fd.append('employee_id', employeeId);
  return api.post(`/ekyc/ocr/${docType}`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const listKycVerifications = (params) => api.get('/ekyc/verifications', { params });
export const getEmployeeKycHistory = (employeeId) =>
  api.get(`/ekyc/verifications/by-employee/${employeeId}`);
export const getKycSummary = () => api.get('/ekyc/summary');
// Mobile OTP (MSG91 — currently MOCKED until DLT template approved)
export const sendMobileOtp = (data) => api.post('/ekyc/mobile-otp/send', data);
export const resendMobileOtp = (data) => api.post('/ekyc/mobile-otp/resend', data);
export const verifyMobileOtp = (data) => api.post('/ekyc/mobile-otp/verify', data);
// Email OTP (Gmail SMTP)
export const sendEmailOtp = (data) => api.post('/ekyc/email-otp/send', data);
export const resendEmailOtp = (data) => api.post('/ekyc/email-otp/resend', data);
export const verifyEmailOtp = (data) => api.post('/ekyc/email-otp/verify', data);
// Aadhaar OTP eKYC (Cashfree)
export const sendAadhaarOtp = (data) => api.post('/ekyc/aadhaar-otp/send', data);
export const verifyAadhaarOtp = (data) => api.post('/ekyc/aadhaar-otp/verify', data);
// Bulk CSV upload + onboarding-gating settings
export const bulkUploadKyc = (file) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/ekyc/bulk-upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getKycSettings = () => api.get('/ekyc/settings');
export const updateKycSettings = (data) => api.put('/ekyc/settings', data);
export const getOnboardingKycStatus = (employeeId) =>
  api.get(`/ekyc/onboarding-status/${encodeURIComponent(employeeId)}`);

// Integration Settings (Cashfree eKYC provider)
export const getCashfreeIntegration = () => api.get('/settings/integrations/cashfree');
export const saveCashfreeIntegration = (data) =>
  api.put('/settings/integrations/cashfree', data);
export const clearCashfreeIntegration = () =>
  api.delete('/settings/integrations/cashfree');
export const testCashfreeConnection = () =>
  api.post('/settings/integrations/cashfree/test-connection');

// BGV — HR endpoints
export const initiateBgvCase = (data) => api.post('/bgv/cases', data);
export const listBgvCases = (params) => api.get('/bgv/cases', { params });
export const getBgvCase = (caseId) => api.get(`/bgv/cases/${caseId}`);
export const updateBgvCheck = (caseId, checkName, data) =>
  api.patch(`/bgv/cases/${caseId}/checks/${checkName}`, data);
export const runBgvIdentityCheck = (caseId) =>
  api.post(`/bgv/cases/${caseId}/run-identity-check`);
export const finalizeBgvCase = (caseId, data) => api.post(`/bgv/cases/${caseId}/finalize`, data);
export const downloadBgvReport = (caseId) =>
  api.get(`/bgv/cases/${caseId}/report`, { responseType: 'blob' });
export const bulkInitiateBgvCases = (candidates, packageName = 'standard') =>
  api.post('/bgv/cases/bulk', { candidates, package: packageName });
export const getBgvDashboardStats = (stuck_days = 7) =>
  api.get('/bgv/dashboard/stats', { params: { stuck_days } });
export const processScheduledBgv = () => api.post('/bgv/process-scheduled');
export const acceptOfferLetter = (offerId, data = {}) =>
  api.post(`/offer-letters/${offerId}/accept`, data);

// BGV — Public/candidate endpoints (no auth header)
export const getBgvCaseFromToken = (token) =>
  api.get('/public/bgv/case-from-token', { params: { token } });
export const sendBgvOtp = (token) => api.post('/public/bgv/otp/send', { token });
export const verifyBgvOtp = (token, otp) => api.post('/public/bgv/otp/verify', { token, otp });
export const submitBgvConsent = (token, typed_name) =>
  api.post('/public/bgv/consent', { token, typed_name });
export const submitBgvForm = (token, form) => api.post('/public/bgv/form', { token, form });

export default api;